import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import {Map} from 'immutable';
import track from 'react-tracking';
import withTracking from 'lib/hocs/withTracking';
import {RANGES} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import {pushQueryParams, removeQueryParams} from 'client/history';
import {MEGA_CLASSES_SIZE} from 'client/constants';
import {getModelForResourceType} from 'resources/modelRegistry';
import {
  MandarkTable,
  Card,
  MenuListItem,
  Dropdown,
  DropdownItem,
  DatePicker,
  Button,
  Icon,
  Fieldset,
  ButtonGroup,
  Text,
  Label,
  Radio,
  LoadingSpinner,
  LoadingSpinnerLong,
  Modal,
  Dialogue
} from '@albert-io/atomic';

import DueDateContent from 'client/Classrooms/shared/DueDateContent/DueDateContent.react';
import {resource} from '@albert-io/json-api-framework/request/builder';

import CreateAssignmentModal from './CreateAssignmentModal.react';
import CreateAssessmentModal from './CreateAssessmentModal.react';

import {LtiCell} from 'client/components/ContentDiscovery/Document/AssignmentSharing/LtiCell.react';

import AssignmentRowOptions from './AssignmentRowOptions.react';
import NoResultsForSelectedDueDates from './NoResultsForSelectedDueDates.react';
import EmptyState from '../../EmptyState/index.react';

import './assignments-list.scss';
import AssignmentContent from '../../../../shared/AssignmentContent/AssignmentContent.react';

// @todo these should only be recorded when the user applies their selection
// or we can segment them. change versus submit

const TrackedDatePickerSelectField = withTracking(DatePicker.SelectField, {
  onChange: {
    component: 'filter_menu',
    content: (e) => e.target.value,
    event: 'change',
    type: 'dropdown item'
  }
});

const TrackedDatePickerStartDateField = withTracking(DatePicker.StartDateField, {
  onChange: {
    component: 'Date filter',
    content: 'Start',
    event: 'change',
    type: 'input'
  }
});

const TrackedDatePickerEndDateField = withTracking(DatePicker.EndDateField, {
  onChange: {
    component: 'Date filter',
    content: 'End',
    event: 'change',
    type: 'input'
  }
});

const TrackedDatePickerApplyBtn = withTracking(DatePicker.ApplyBtn, {
  onClick: {
    component: 'Date filter',
    content: (e) =>
      `${e.target.dataset.range} ${e.target.dataset.start} ${e.target.dataset.end}${
        e.target.dataset.valid === 'false' ? ' was invalid' : ''
      }`,
    event: 'click',
    type: 'button'
  }
});

const TotalEntries = ({query, className, selected, ...rest}) => {
  const [total, setTotal] = useState('');
  const isReady = query.isResourceReady();
  useEffect(() => {
    async function requestTotal() {
      await query.getResourcePromise();
      setTotal(query.getResourceMetadata().getIn(['page', 'total_entries']));
    }
    requestTotal();
  }, [isReady, query]);
  return (
    <Text
      bold
      size='s'
      className={className}
      color={selected ? 'blue' : 'black'}
      emphasis={selected ? 'high' : 'lowest'}
      {...rest}
    >
      {total}
    </Text>
  );
};

TotalEntries.propTypes = {
  query: PropTypes.string,
  className: PropTypes.string,
  selected: PropTypes.bool
};

function getSearchFilters(query, term) {
  return query.filter({
    name: {
      case_insensitive_substring: term
    }
  });
}

function getAllAssignmentsQuery(classroomId) {
  return resource('assignments_v3').mandarkEndpoint(['assignments_v3']).filter({
    classrooms_v1: classroomId
  });
}

@track({component: 'table', feature: 'Assignments List'})
class AssignmentsList extends React.Component {
  static propTypes = {
    location: PropTypes.object,
    tracking: PropTypes.object,
    params: PropTypes.object,
    classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1'))
  };

  constructor() {
    super();

    this.state = {
      applyCounter: 0,
      type: '',
      showAssignModal: false,
      showAssessModal: false,
      showNoSubmissionsModal: false
    };
  }

  componentDidMount() {
    const {location} = this.props;
    const {type = ''} = location.query;
    this.setState({type});
  }

  componentDidUpdate(prevProps) {
    const {location} = this.props;
    const {type = ''} = location.query;
    const {location: prevLocation} = prevProps;
    const {type: prevType = ''} = prevLocation.query;
    if (type !== prevType) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({type});
    }
  }

  trackSort = (sortVal) => {
    const formattedSortVal = sortVal === 'name' ? 'assignment' : sortVal;
    this.props.tracking.trackEvent({
      event: 'click',
      type: 'sort',
      content: formattedSortVal
    });
  };

  render() {
    const {query} = this.props.location;
    const {range = RANGES.ALL_TIME, start = '', end = '', assignmentStatus = '', type = ''} = query;
    const classroomId = this.props.params.classId;
    const {showAssignModal, showAssessModal, applyCounter} = this.state;
    const schoolHasLtiDeployment = this.props.classroom.getMeta().hasLtiDeployment();

    const wantedFilters = ['range', 'type'];
    const filtersCount = Object.keys(query).reduce((sum, key) => {
      // Check that the value at that key exists and is a filter we want
      if (query[key] && wantedFilters.includes(key)) {
        return sum + 1;
      }
      return sum;
    }, 0);

    const assignmentQuery = resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .include('sections_v1')
      .include('students_v2')
      .include('classrooms_v1')
      .withMeta('assignment_v3')
      .meta({
        context: {
          assignment: {
            stats: true
          }
        }
      })
      .filter({
        classrooms_v1: classroomId
      })
      .filter(
        {
          assignment_type: type
        },
        !!type
      )
      .filter(
        {
          due_date: {
            greater_than_inclusive: moment(start).startOf('day').subtract(1, 'second').valueOf(),
            less_than_inclusive: moment(end).endOf('day').valueOf()
          }
        },
        start && end
      )
      // CLOSED:
      // The student has submitted the assignment
      // OR (the assignment is past due and late submissions are not allowed)
      .filter(
        {
          any_of: [
            {
              'meta.count_of_incomplete_students': 0
            },
            {
              due_date: {
                less_than: 'now'
              },
              allow_late_submissions: false
            }
          ]
        },
        assignmentStatus === 'closed'
      )
      // OPEN: status for 'open' is not set to make it the default tab when query prop is used
      // Start Date < Now
      // AND the student hasn't submitted
      // AND [(Due date < Now )OR (Due date > Now AND Late submissions allowed)]
      .filter(
        {
          all_of: [
            {
              any_of: [
                {
                  start_date: {
                    less_than: 'now'
                  }
                },
                {
                  start_date: {
                    null: true
                  }
                }
              ]
            },
            {
              any_of: [
                {
                  due_date: {
                    greater_than: 'now'
                  }
                },
                {
                  due_date: {
                    less_than: 'now'
                  },
                  allow_late_submissions: true
                }
              ]
            }
          ],
          'meta.count_of_incomplete_students': {
            not: 0
          }
        },
        assignmentStatus === 'open'
      )
      .filter(
        {
          start_date: {
            greater_than: 'now',
            null: false
          }
        },
        assignmentStatus === 'scheduled'
      )
      .filter({
        included: {
          students_v2: {
            classrooms_v1: classroomId
          }
        }
      });

    const allAssignmentQuery = resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .include('classrooms_v1')
      .filter({
        classrooms_v1: classroomId
      })
      .filter(
        {
          name: {case_insensitive_substring: this.props.location.query.search}
        },
        !!this.props.location.query.search
      )
      .pageSize(1);

    const openAssignmentQuery = resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .include('classrooms_v1')
      .filter({
        classrooms_v1: classroomId
      })
      .filter({
        all_of: [
          {
            any_of: [
              {
                start_date: {
                  less_than: 'now'
                }
              },
              {
                start_date: {
                  null: true
                }
              }
            ]
          },
          {
            any_of: [
              {
                due_date: {
                  greater_than: 'now'
                }
              },
              {
                due_date: {
                  less_than: 'now'
                },
                allow_late_submissions: true
              }
            ]
          }
        ],
        'meta.count_of_incomplete_students': {
          not: 0
        }
      })
      .filter(
        {
          name: {case_insensitive_substring: this.props.location.query.search}
        },
        !!this.props.location.query.search
      )
      .pageSize(1);

    const closedAssignmentQuery = resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .include('classrooms_v1')
      .filter({
        classrooms_v1: classroomId
      })
      .filter({
        any_of: [
          {
            'meta.count_of_incomplete_students': 0
          },
          {
            due_date: {
              less_than: 'now'
            },
            allow_late_submissions: false
          }
        ]
      })
      .filter(
        {
          name: {case_insensitive_substring: this.props.location.query.search}
        },
        !!this.props.location.query.search
      )
      .pageSize(1);

    const scheduledAssignmentQuery = resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .include('classrooms_v1')
      .filter({
        classrooms_v1: classroomId
      })
      .filter({
        start_date: {
          greater_than: 'now',
          null: false
        }
      })
      .filter(
        {
          name: {case_insensitive_substring: this.props.location.query.search}
        },
        !!this.props.location.query.search
      )
      .pageSize(1);

    const chosenDate = Map({
      id: range || RANGES.ALL_TIME,
      start,
      end,
      value: range || RANGES.ALL_TIME
    });

    return (
      <div className='assignments-list__wrapper'>
        <Card className='assignments-list__wrapper-sidebar u-mar-b_2 u-pad_2' shadow='default'>
          <Text as='div' className='u-pad_2 u-color_slate-500' size='m' bold>
            Assignments
          </Text>
          <MenuListItem
            text='All'
            clickable
            onClick={() => {
              removeQueryParams('assignmentStatus');
            }}
            selected={assignmentStatus === ''}
          >
            <MenuListItem.Text>All</MenuListItem.Text>
            <TotalEntries query={allAssignmentQuery} />
          </MenuListItem>
          <MenuListItem
            text='Scheduled'
            clickable
            onClick={() => pushQueryParams({assignmentStatus: 'scheduled'})}
            selected={assignmentStatus === 'scheduled'}
          >
            <MenuListItem.Text>Scheduled</MenuListItem.Text>
            <TotalEntries query={scheduledAssignmentQuery} bold className='u-mar-l_2' />
          </MenuListItem>
          <MenuListItem
            text='Open'
            clickable
            onClick={() => pushQueryParams({assignmentStatus: 'open'})}
            selected={assignmentStatus === 'open'}
          >
            <MenuListItem.Text>Open</MenuListItem.Text>
            <TotalEntries query={openAssignmentQuery} />
          </MenuListItem>
          <MenuListItem
            text='Closed'
            clickable
            onClick={() => pushQueryParams({assignmentStatus: 'closed'})}
            selected={assignmentStatus === 'closed'}
          >
            <MenuListItem.Text>Closed</MenuListItem.Text>
            <TotalEntries query={closedAssignmentQuery} />
          </MenuListItem>
        </Card>
        <div className='assignments-list__table'>
          <MandarkTable
            query={assignmentQuery}
            searchFilters={getSearchFilters}
            searchPlaceholder='Search assignments'
            defaultSortDirection='ASCENDING'
            rowLinkFunc={(assignment) => {
              return `/classes/${classroomId}/assignments/${assignment.getId()}/student-detail`;
            }}
            persistSearch
            persistPage
            theme='list'
            title='Assignments'
            customEmptyTable={<NoResultsForSelectedDueDates query={query} />}
            buttonGroup={[
              <Dropdown
                trigger={
                  <Button
                    color='secondary'
                    variant='outlined'
                    label='filters'
                    className='u-mar-r_1'
                  >
                    <Icon icon={['far', 'filter']} className='u-mar-r_1' />
                    Filters {filtersCount !== 0 && `(${filtersCount})`}
                  </Button>
                }
                onDismiss={() => {
                  this.setState({type});
                }}
                positionFixed
                key={`${this.props.location.search}${applyCounter}`}
                className='assignments-list__filter-dropdown'
              >
                <DatePicker.Provider
                  onSubmit={(params) => {
                    const prevQueryObj = this.props.location.query;
                    const queryWithType = {
                      ...params,
                      range: params.range === 'All-time' ? '' : params.range,
                      type: this.state.type
                    };
                    const nextQueryObj = Object.keys(queryWithType).reduce((prev, curr) => {
                      if (queryWithType[curr]) {
                        prev[curr] = queryWithType[curr];
                      } else if (!queryWithType[curr] && prevQueryObj[curr]) {
                        prev[curr] = queryWithType[curr];
                      }
                      return prev;
                    }, {});
                    pushQueryParams(nextQueryObj);
                  }}
                  defaultValue={chosenDate}
                  className='assignments-list__filter-remove-hover'
                >
                  <DropdownItem preventDefaultClickHandler={false}>
                    <TrackedDatePickerSelectField className='u-mar-b_2' label='Due Date Range' />
                    {/* @todo use CSS rather than this div */}
                    <div>
                      <TrackedDatePickerStartDateField />
                      <DatePicker.ArrowIcon className='u-mar-lr_1' />
                      <TrackedDatePickerEndDateField />
                    </div>
                    <DatePicker.ErrorMsg />
                  </DropdownItem>
                  <div className='a-divider u-mar-t_1 u-mar-b_2 u-mar-lr_2' />
                  <Fieldset>
                    <Text bold className='u-pad-lr_2 u-pad-t_2'>
                      Assignment Type
                    </Text>
                    <DropdownItem
                      preventDefaultClickHandler={false}
                      className='assignments-list__filter-remove-hover u-pad-b_0'
                    >
                      <Label>
                        <Radio
                          className='u-mar-r_2'
                          onChange={(e) => {
                            this.setState({
                              type: e.target.checked ? 'assignment' : 'exam'
                            });
                          }}
                          checked={this.state.type === 'assignment'}
                        />
                        Assignment
                      </Label>
                    </DropdownItem>
                    <DropdownItem
                      preventDefaultClickHandler={false}
                      className='assignments-list__filter-remove-hover'
                    >
                      <Label>
                        <Radio
                          className='u-mar-r_2'
                          onChange={(e) => {
                            this.setState({
                              type: e.target.checked ? 'exam' : 'assignment'
                            });
                          }}
                          checked={this.state.type === 'exam'}
                        />
                        Assessment
                      </Label>
                    </DropdownItem>
                    <div className='a-divider u-mar-t_1 u-mar-lr_2' />
                    <DropdownItem preventDefaultClickHandler={false}>
                      <ButtonGroup align='right' className='u-display_inline'>
                        <Button
                          className='u-mar-r_1'
                          aria-label='reset'
                          variant='text'
                          onClick={() => {
                            removeQueryParams('start', 'end', 'range', 'type');
                            this.setState((state) => ({
                              applyCounter: state.applyCounter + 1,
                              type: ''
                            }));
                          }}
                        >
                          Reset
                        </Button>
                        <TrackedDatePickerApplyBtn
                          aria-label='apply'
                          onClick={() => {
                            this.setState((state) => ({applyCounter: state.applyCounter + 1}));
                          }}
                        />
                      </ButtonGroup>
                    </DropdownItem>
                  </Fieldset>
                </DatePicker.Provider>
              </Dropdown>,
              <Dropdown
                trigger={
                  <Button>
                    Assign
                    <Icon icon='caret-down' className='u-mar-l_1' />
                  </Button>
                }
              >
                <DropdownItem
                  preventDefaultClickHandler={false}
                  onClick={() => this.setState({showAssignModal: true})}
                >
                  Assignment
                </DropdownItem>
                <DropdownItem
                  preventDefaultClickHandler={false}
                  onClick={() => this.setState({showAssessModal: true})}
                >
                  Assessment
                </DropdownItem>
              </Dropdown>
            ]}
          >
            <MandarkTable.Column
              heading='Assignment Name'
              sortBy='name'
              content={(assignment) => <AssignmentContent assignment={assignment} />}
              className='assignments-list__table-column'
            />
            <MandarkTable.Column
              defaultSortParam
              heading='Due Date'
              sortBy='due_date,name'
              content={(assignment) => (
                <DueDateContent assignment={assignment} classroomId={classroomId} />
              )}
              className='assignments-list__table-column'
              defaultColumnSortDirection='DESCENDING'
            />
            <MandarkTable.Column
              heading='Completed'
              content={(assignment) => {
                const numberSubmitted = assignment.getMeta().getCountOfCompletedStudents();
                const numberNotSubmitted = assignment.getMeta().getCountOfIncompleteStudents();
                const total = numberSubmitted + numberNotSubmitted;
                return <Text> {`${numberSubmitted}/${total}`}</Text>;
              }}
            />
            {schoolHasLtiDeployment && (
              <MandarkTable.Column
                heading='LMS'
                content={(assignment) => {
                  const {classroom} = this.props;
                  return (
                    <LtiCell
                      assignment={assignment}
                      classroom={classroom}
                      school={classroom.getSchool()}
                    />
                  );
                }}
              />
            )}
            <MandarkTable.Column
              noClamp
              content={(assignmentToDelete) => (
                <AssignmentRowOptions assignment={assignmentToDelete} classroomId={classroomId} />
              )}
            />
          </MandarkTable>
        </div>
        {showAssignModal && (
          <CreateAssignmentModal
            handleClose={() => this.setState({showAssignModal: false})}
            classroomId={classroomId}
          />
        )}
        {showAssessModal && (
          <CreateAssessmentModal
            handleClose={() => this.setState({showAssessModal: false})}
            classroomId={classroomId}
          />
        )}
        {this.state.showNoSubmissionsModal ? (
          <Modal
            ariaLabel='This assignment has not yet received any submissions. '
            className='u-display_flex u-align-items_center u-justify-content_center'
          >
            {({modalContentStyle}) => (
              <Dialogue hideCloseBtn className={modalContentStyle} inModal size='s'>
                <Dialogue.Body align='left' className='u-color_slate-700'>
                  <Text as='p'>This assignment has not yet received any submissions.</Text>
                  <Text as='p'>Try again after student guesses have been submitted.</Text>
                </Dialogue.Body>
                <Dialogue.BtnGroup>
                  <Button
                    variant='solid'
                    color='primary'
                    onClick={() => {
                      this.setState({showNoSubmissionsModal: false});
                    }}
                  >
                    Dismiss
                  </Button>
                </Dialogue.BtnGroup>
              </Dialogue>
            )}
          </Modal>
        ) : null}
      </div>
    );
  }
}

const AssignmentsListWrapper = ({location, params, classroom}) => {
  const allAssignments = getAllAssignmentsQuery(params.classId)
    .filter(
      {
        name: {case_insensitive_substring: location.query.search}
      },
      !!location.query.search
    )
    .pageSize(1);
  const {isReady} = useAreLatestQueriesReady([allAssignments]);

  if (!isReady) {
    if (classroom.getMeta().getCountOfStudents() > MEGA_CLASSES_SIZE) {
      return <LoadingSpinnerLong />;
    }
    return <LoadingSpinner size={3} />;
  }

  // no assignments created yet
  if (
    allAssignments.getResourceMetadata().getIn(['page', 'total_entries']) === 0 &&
    !location.query.search
  ) {
    return (
      <EmptyState
        title='No assignments'
        body='Student data will appear after practice has been assigned to students. Visit the library to assign students practice.'
      />
    );
  }

  return <AssignmentsList location={location} params={params} classroom={classroom} />;
};

AssignmentsListWrapper.propTypes = {
  location: PropTypes.object,
  params: PropTypes.object,
  classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1'))
};

export default AssignmentsListWrapper;
